/**
 * useBoostPolicyQA — manages the AI Q&A conversation session for the Boost Policy flow.
 * Handles memory fetching, streaming turns, and context-complete detection.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { QAMessage, OrganizationProfileData } from '../types/boostPolicy';
import { getOrgMemory, appendToOrgMemory, ensureCategoryExists, streamQATurn } from '../services/orgMemoryService';

export type QAStatus = 'loading' | 'streaming' | 'waiting' | 'done' | 'error';

type UseBoostPolicyQAOptions = {
  orgId: string;
  orgProfile: OrganizationProfileData | null;
  documentTitle: string;
  policyName: string;
  relevantContext: string[];
  documentTags: string[];
  /**
   * Safety backstop: max number of user answers before the session is force-completed.
   * The skill normally self-terminates earlier via [[CONTEXT_COMPLETE]] once answers
   * stop changing the policy ("cap by information value"). Set high enough to allow the
   * deep, tiered Q&A the Boost Policy Q&A skill targets (~8–15 questions for broad policies).
   */
  maxTurns?: number;
};

export type QASessionPair = { question: string; answer: string };

type UseBoostPolicyQAReturn = {
  messages: QAMessage[];
  status: QAStatus;
  isContextComplete: boolean;
  memoryContent: string;
  sessionPairs: QASessionPair[];
  submitAnswer: (answer: string) => Promise<void>;
  error: string | null;
};

export const useBoostPolicyQA = ({
  orgId,
  orgProfile,
  documentTitle,
  policyName,
  relevantContext,
  documentTags,
  maxTurns = 15,
}: UseBoostPolicyQAOptions): UseBoostPolicyQAReturn => {
  const [messages, setMessages] = useState<QAMessage[]>([]);
  const [status, setStatus] = useState<QAStatus>('loading');
  const [isContextComplete, setIsContextComplete] = useState(false);
  const [memoryContent, setMemoryContent] = useState('');
  const [sessionPairs, setSessionPairs] = useState<QASessionPair[]>([]);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // Track conversation turns for the stateless backend (excludes the streaming placeholder)
  const historyRef = useRef<{ role: 'ai' | 'user'; text: string }[]>([]);
  const userAnswerCount = useRef(0);
  const initializedRef = useRef(false);

  const streamNextTurn = useCallback(
    async (history: { role: 'ai' | 'user'; text: string }[], currentMemory: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('streaming');

      // Add a streaming placeholder message
      const placeholderTimestamp = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: '', timestamp: placeholderTimestamp, isStreaming: true },
      ]);

      let accumulated = '';
      let completed = false;
      let didComplete = false;

      try {
        await streamQATurn(
          {
            conversationHistory: history,
            orgContext: orgProfile,
            documentTitle,
            policyName,
            memoryContent: currentMemory,
            relevantContext,
            documentTags,
          },
          (event) => {
            if (event.type === 'content') {
              accumulated += event.text;
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.isStreaming) {
                  updated[updated.length - 1] = { ...last, text: accumulated };
                }
                return updated;
              });
            } else if (event.type === 'done') {
              completed = true;
              if (event.isComplete) {
                didComplete = true;
                setIsContextComplete(true);
              }
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          },
          orgId,
          controller.signal,
        );

        // Finalise the streaming message
        const finalTimestamp = new Date().toISOString();
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.isStreaming) {
            updated[updated.length - 1] = {
              ...last,
              text: accumulated,
              isStreaming: false,
              timestamp: finalTimestamp,
            };
          }
          return updated;
        });

        // Append finalised AI turn to history ref
        historyRef.current = [...history, { role: 'ai', text: accumulated }];

        // Use the local flag — reading state here would be stale
        setStatus(didComplete ? 'done' : 'waiting');
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        console.error('[useBoostPolicyQA] Stream error:', err);
        setError('Failed to get the next question. Please try again.');
        setStatus('error');
        // Remove the streaming placeholder on error
        setMessages((prev) => prev.filter((m) => !m.isStreaming));
      }
    },
    [orgId, orgProfile, documentTitle, policyName, relevantContext, documentTags],
  );

  // On mount: fetch memory then stream first question
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const init = async () => {
      try {
        // Ensure category exists in memory for this policy's tags — even if no Q&A happens
        if (documentTags.length) {
          await ensureCategoryExists(documentTags, orgId, documentTitle).catch(() => {});
        }
        const memory = await getOrgMemory(orgId);
        setMemoryContent(memory);
        historyRef.current = [];
        userAnswerCount.current = 0;
        await streamNextTurn([], memory);
      } catch (err) {
        console.error('[useBoostPolicyQA] Init error:', err);
        setError('Could not load your memory. Please refresh and try again.');
        setStatus('error');
      }
    };

    init();

    return () => {
      abortRef.current?.abort();
    };
  }, []); // intentionally empty — runs once on mount

  const submitAnswer = useCallback(
    async (answer: string) => {
      if (!answer.trim() || status === 'streaming') return;

      const timestamp = new Date().toISOString();

      // Find the last AI question to save as the Q
      const lastAiMessage = [...messages].reverse().find((m) => m.role === 'ai');
      const question = lastAiMessage?.text ?? '';

      // Add user message to UI
      setMessages((prev) => [...prev, { role: 'user', text: answer.trim(), timestamp }]);

      // Track this session's Q&A pair for display in step 4
      if (question) {
        setSessionPairs((prev) => [...prev, { question, answer: answer.trim() }]);
      }

      // Persist to memory.md under the correct tag section + AI-classified category
      if (question) {
        try {
          await appendToOrgMemory(question, answer.trim(), orgId, documentTags, documentTitle);
          const updatedMemory = await getOrgMemory(orgId);
          setMemoryContent(updatedMemory);
        } catch (err) {
          console.warn('[useBoostPolicyQA] Memory save failed (non-critical):', err);
        }
      }

      // Append user turn to history
      const newHistory: { role: 'ai' | 'user'; text: string }[] = [
        ...historyRef.current,
        { role: 'user', text: answer.trim() },
      ];
      historyRef.current = newHistory;
      userAnswerCount.current += 1;

      // Force complete if max turns reached
      if (userAnswerCount.current >= maxTurns) {
        setIsContextComplete(true);
        setStatus('done');
        return;
      }

      // Stream AI's next response
      const latestMemory = await getOrgMemory(orgId).catch(() => memoryContent);
      await streamNextTurn(newHistory, latestMemory);
    },
    [status, messages, orgId, maxTurns, memoryContent, streamNextTurn],
  );

  return { messages, status, isContextComplete, memoryContent, sessionPairs, submitAnswer, error };
};
